import { startOfHour } from 'date-fns'
import {
    FC,
    FormEvent,
    ReactElement,
    useRef,
    useState,
} from 'react'

import { DateInput, FormActionButtons, Textarea, TextInput, TimeInput } from '../../../compositions'
import { AddChangeLogEntryFormData } from '../../../entities/ChangeLog/ChangeLog'
import { getCurrentAppVersion } from '../../../entities/ChangeLog/ChangeLogHelpers'
import { transformDateAndTimeToChangeLogEntryDateString } from '../../../entities/ChangeLog/ChangeLogTransformers'
import { formatDate } from '../../../helpers/date'
import trans from '../../../helpers/trans'
import { isVersionGreaterThan } from '../../../helpers/version'
import { DateFormat } from '../../../types/dateFormatTypes'
import { ModalFormProps } from '../../../types/modalFormTypes'
import {
    AddChangeLogEntryFormErrors,
    validateAddChangeLogEntryFormData,
} from './validations/addChangeLogEntryFormValidation'

import './AddChangeLogEntryForm.scss'

interface AddChangeLogEntryFormProps extends ModalFormProps<AddChangeLogEntryFormData> {
    onSubmit: (addChangeLogEntryFormData: AddChangeLogEntryFormData) => void
}

export const AddChangeLogEntryForm: FC<AddChangeLogEntryFormProps> = ({
    isLoading,
    onSubmit,
    onCancel,
}): ReactElement => {
    const [formData, setFormData] = useState<AddChangeLogEntryFormData>({
        version: getCurrentAppVersion(),
        date: '',
        intro: '',
        changes: '',
        popupMessage: '',
    })
    const [formErrors, setFormErrors] = useState<AddChangeLogEntryFormErrors>({})
    const [dateInputValue, setDateInputValue] = useState<Date>(new Date())
    const [timeInputValue, setTimeInputValue] = useState<string>(
        formatDate(startOfHour(new Date()), DateFormat.inputTime),
    )

    const formRef = useRef<HTMLFormElement>(null)

    const isUnreleasedVersion = isVersionGreaterThan(formData.version, getCurrentAppVersion())

    const handleChange = (key: keyof AddChangeLogEntryFormData) => (value: string): void => {
        setFormData({ ...formData, [key]: value })
    }

    const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault()

        const formDataToSubmit: AddChangeLogEntryFormData = {
            ...formData,
            date: transformDateAndTimeToChangeLogEntryDateString(dateInputValue, timeInputValue),
        }

        const [errors, hasErrors] = validateAddChangeLogEntryFormData(formDataToSubmit)
        setFormErrors(errors)

        if (! hasErrors) {
            setFormData(formDataToSubmit)
            onSubmit(formDataToSubmit)
        }
    }

    return (
        <form ref={formRef} onSubmit={handleSubmit} className="add-change-log-entry-form">
            <div className="add-change-log-entry-form__row">
                <div className="add-change-log-entry-form__col">
                    <TextInput
                        label={trans('containers.forms.addChangeLogEntryForm.version')}
                        type="text"
                        value={formData.version}
                        error={formErrors.version}
                        onChange={handleChange('version')}
                    />
                    {! formErrors.version && isUnreleasedVersion && (
                        <p className="add-change-log-entry-form__info">
                            {trans(
                                'containers.forms.addChangeLogEntryForm.futureVersionWarning',
                                { version: formData.version },
                            )}
                        </p>
                    )}
                </div>
            </div>
            <div className="add-change-log-entry-form__row">
                <div className="add-change-log-entry-form__col">
                    <DateInput
                        label={trans('containers.forms.addChangeLogEntryForm.date')}
                        value={dateInputValue}
                        onChange={setDateInputValue}
                    />
                </div>
                <div className="add-change-log-entry-form__col">
                    <TimeInput
                        label={trans('containers.forms.addChangeLogEntryForm.time')}
                        value={timeInputValue}
                        onChange={setTimeInputValue}
                    />
                </div>
            </div>
            <div className="add-change-log-entry-form__row">
                <div className="add-change-log-entry-form__col">
                    <TextInput
                        label={trans('containers.forms.addChangeLogEntryForm.intro')}
                        type="text"
                        value={formData.intro}
                        error={formErrors.intro}
                        onChange={handleChange('intro')}
                    />
                </div>
            </div>
            <div className="add-change-log-entry-form__row">
                <div className="add-change-log-entry-form__col">
                    <Textarea
                        label={trans('containers.forms.addChangeLogEntryForm.changes')}
                        value={formData.changes}
                        error={formErrors.changes}
                        onChange={handleChange('changes')}
                    />
                </div>
            </div>
            <div className="add-change-log-entry-form__row">
                <div className="add-change-log-entry-form__col">
                    <Textarea
                        label={trans('containers.forms.addChangeLogEntryForm.popupMessage')}
                        value={formData.popupMessage}
                        error={formErrors.popupMessage}
                        onChange={handleChange('popupMessage')}
                    />
                </div>
            </div>
            <FormActionButtons
                isLoading={isLoading}
                disabled={! formData.version || ! formData.changes}
                onCancelClick={onCancel}
            />
        </form>
    )
}
